import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronsUpDown, Building2, Check, Plus } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrgSwitcher
 * -----------
 * Dropdown that lists every organization the caller belongs to. Selecting one
 * calls /api/me/org/switch which:
 *   - validates membership
 *   - updates user.current_org_id
 *   - returns a new JWT scoped to the new org
 *
 * The new token replaces localStorage['token'] and the page reloads so all
 * cached SWR/state is dropped (cleanest way to switch tenant context).
 *
 * Hidden when the user belongs to exactly 1 org (the common solo-founder case).
 */
const OrgSwitcher = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const [me, list] = await Promise.all([
          axios.get(`${API}/me/org`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/me/organizations`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (cancelled) return;
        setCurrentId(me.data.organization_id);
        setOrgs(list.data.organizations || []);
      } catch {
        // silent fail — switcher just won't render
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Don't render anything if there's only one org — solo founders don't need
  // a switcher cluttering their nav.
  if (orgs.length <= 1) return null;

  const switchTo = async (orgId) => {
    if (orgId === currentId) return;
    setSwitching(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/me/org/switch`,
        { organization_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data.access_token) {
        localStorage.setItem('token', r.data.access_token);
        toast.success('Switched organization — reloading…');
        // Hard reload so per-org dashboards re-fetch from scratch.
        setTimeout(() => window.location.reload(), 400);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not switch organization');
      setSwitching(false);
    }
  };

  const current = orgs.find((o) => o.id === currentId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-white transition-colors"
        disabled={switching}
        data-testid="org-switcher-trigger"
      >
        <Building2 className="w-4 h-4 text-emerald-400" />
        <span className="font-semibold truncate max-w-[140px]">
          {current?.name || 'Organization'}
        </span>
        <ChevronsUpDown className="w-3.5 h-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 bg-slate-900 border-white/10"
        data-testid="org-switcher-content"
      >
        <DropdownMenuLabel className="text-slate-400 text-xs uppercase tracking-wider">
          Switch organization
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        {orgs.map((o) => (
          <DropdownMenuItem
            key={o.id}
            onClick={() => switchTo(o.id)}
            className="flex items-center gap-2 cursor-pointer hover:bg-white/5"
            data-testid={`org-switcher-item-${o.id}`}
          >
            <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white truncate">{o.name}</div>
              <div className="text-xs text-slate-400">
                {o.role}{o.is_personal ? ' · Personal' : ''}
              </div>
            </div>
            {o.id === currentId && (
              <Check className="w-4 h-4 text-emerald-400 shrink-0" />
            )}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem
          onClick={() => navigate('/organization')}
          className="text-emerald-400 hover:bg-white/5 cursor-pointer"
          data-testid="org-switcher-manage-link"
        >
          <Plus className="w-4 h-4 mr-2" />
          Manage organizations
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OrgSwitcher;
